import { Outlet } from "react-router-dom";
import { AlertCircle, Files, LayoutDashboard, Table2 } from "lucide-react";
import { Skeleton } from "~/components/ui/skeleton";
import useNextGenHeader from "~/contexts/nextgen-header";
import { useDocumentTitle } from "~/hooks/useDocumentTitle";
import { buildEntityBreadcrumbs } from "~/pages/manifold-nextgen/breadcrumbs";
import { SectionNavLink } from "~/pages/manifold-nextgen/components/SectionNavLink";
import { SectionSidebar } from "~/pages/manifold-nextgen/components/SectionSidebar";
import usePersonalDataset from "~/pages/manifold-nextgen/hooks/usePersonalDataset";
import { ROUTE_NEXTGEN_DATASETS } from "~/router/constants";

const PERSONAL_DATASET_NAME = "My Dataset";

export const PersonalDatasetDetail = () => {
  const { data: dataset, error, isLoading } = usePersonalDataset();

  useDocumentTitle(PERSONAL_DATASET_NAME);
  useNextGenHeader({
    breadcrumbs: buildEntityBreadcrumbs({
      rootHref: ROUTE_NEXTGEN_DATASETS,
      entityName: PERSONAL_DATASET_NAME,
    }),
  });

  if (isLoading) {
    return (
      <div role="status" aria-label="Loading dataset" className="flex min-h-full">
        <div className="w-56 shrink-0 border-r border-border px-4 py-3">
          {Array.from({ length: 2 }).map((_, i) => (
            <Skeleton key={i} className="mx-1 my-1 h-8 rounded-md" />
          ))}
        </div>
        <div className="flex-1 p-6">
          <Skeleton className="h-64 rounded-lg" />
        </div>
      </div>
    );
  }

  if (error || !dataset) {
    return (
      <div
        role="alert"
        className="flex flex-col items-center gap-3 py-16 text-center text-destructive"
      >
        <AlertCircle className="size-8" aria-hidden="true" />
        <p className="text-sm font-medium">Failed to load your personal dataset</p>
      </div>
    );
  }

  return (
    <div className="flex h-full overflow-hidden">
      <SectionSidebar ariaLabel="Personal dataset sections">
        <div className="flex flex-col gap-1 px-4 py-3">
          <SectionNavLink
            to="overview"
            end
            icon={<LayoutDashboard className="size-4" />}
            label="Overview"
          />
          <SectionNavLink to="tables" end icon={<Table2 className="size-4" />} label="Tables" />
          <SectionNavLink to="files" end icon={<Files className="size-4" />} label="Files" />
        </div>
      </SectionSidebar>

      <div className="flex-1 overflow-y-auto p-6">
        <Outlet />
      </div>
    </div>
  );
};
